// /src/utils/plantStore.js
import { create } from 'zustand'

const getApiUrl = (endpoint) => {
  // Use the current domain for API calls
  const baseUrl = window.location.origin;
  return `${baseUrl}/api${endpoint}`;
};

const usePlantStore = create((set, get) => ({
  plants: [],
  isLoading: false,
  error: null,

  // Simple function to get plants
  fetchPlants: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(getApiUrl('/plants'));
      if (response.ok) {
        const data = await response.json();
        const plants = data.plants || [];
        
        // Simple deduplication - keep only unique plants
        const uniquePlants = plants.filter((plant, index, self) => 
          index === self.findIndex(p => 
            p.name.toLowerCase().trim() === plant.name.toLowerCase().trim() &&
            p.scientificName.toLowerCase().trim() === plant.scientificName.toLowerCase().trim()
          )
        );
        
        console.log('📊 Plants loaded from API:', uniquePlants.length);
        set({ plants: uniquePlants, isLoading: false });
        localStorage.setItem('localPlants', JSON.stringify(uniquePlants));
      } else {
        console.log('⚠️ API not available, using localStorage');
        // Fallback to localStorage
        const localPlants = JSON.parse(localStorage.getItem('localPlants') || '[]');
        set({ plants: localPlants, isLoading: false });
      }
    } catch (error) {
      console.error('Error fetching plants:', error);
      // Fallback to localStorage
      const localPlants = JSON.parse(localStorage.getItem('localPlants') || '[]');
      set({ plants: localPlants, error: null, isLoading: false });
    }
  },

  addPlant: async (plant) => {
    try {
      const formData = new FormData();
      formData.append('operation', 'add');
      formData.append('name', plant.name?.trim() || '');
      formData.append('scientificName', plant.scientificName?.trim() || '');
      formData.append('category', plant.category || 'tree');
      formData.append('description', plant.description || '');
      
      // Handle images
      if (plant.images && Array.isArray(plant.images)) {
        formData.append('images', JSON.stringify(plant.images));
      } else if (plant.image) {
        formData.append('imageUrl', plant.image);
      }
      
      formData.append('habitat', plant.habitat || '');
      formData.append('careLevel', plant.careLevel || 'Easy');

      const response = await fetch(getApiUrl('/plants'), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Add new plant to state
        set((state) => {
          const newPlants = [result.plant, ...state.plants];
          localStorage.setItem('localPlants', JSON.stringify(newPlants));
          return { plants: newPlants };
        });
        
        return result.plant;
      } else {
        throw new Error(result.error || 'Failed to add plant');
      }
      
    } catch (error) {
      console.error('Error adding plant:', error);
      throw error;
    }
  },

  updatePlant: async (id, plant) => {
    try {
      const formData = new FormData();
      formData.append('operation', 'update');
      formData.append('id', id);
      formData.append('name', plant.name?.trim() || '');
      formData.append('scientificName', plant.scientificName?.trim() || '');
      formData.append('category', plant.category || 'tree');
      formData.append('description', plant.description || '');
      
      if (plant.images && Array.isArray(plant.images)) {
        formData.append('images', JSON.stringify(plant.images));
      } else if (plant.image) {
        formData.append('imageUrl', plant.image);
      }
      
      formData.append('habitat', plant.habitat || '');
      formData.append('careLevel', plant.careLevel || 'Easy');

      const response = await fetch(getApiUrl('/plants'), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        set((state) => {
          const updatedPlants = state.plants.map(p => 
            p.id === id ? result.plant : p
          );
          localStorage.setItem('localPlants', JSON.stringify(updatedPlants));
          return { plants: updatedPlants };
        });
        
        return result.plant;
      } else {
        throw new Error(result.error || 'Failed to update plant');
      }
      
    } catch (error) {
      console.error('Error updating plant:', error);
      throw error;
    }
  },

  deletePlant: async (id) => {
    try {
      const formData = new FormData();
      formData.append('operation', 'delete');
      formData.append('id', id);

      const response = await fetch(getApiUrl('/plants'), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        set((state) => {
          const updatedPlants = state.plants.filter(p => p.id !== id);
          localStorage.setItem('localPlants', JSON.stringify(updatedPlants));
          return { plants: updatedPlants };
        });
      } else {
        throw new Error(result.error || 'Failed to delete plant');
      }
      
    } catch (error) {
      console.error('Error deleting plant:', error);
      throw error;
    }
  },

  clearPlants: () => {
    localStorage.removeItem('localPlants');
    set({ plants: [] });
  }
}))

export default usePlantStore